// Contrail V3.5.0 — Alerts manager (Events tab → "Alerts"): every saved alert of a company in one place,
// filter by object, switch on/off, edit, delete, or create a new alert for any object.
import { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "../../lib/supabase";
import { ALERT_LABEL, channelsText, orgApi, ruleSummary, type AlertRule, type Zone } from "../../lib/org";
import { Drawer, Notice } from "../admin/ui";
import AlertRuleForm, { type DevLite } from "./AlertRuleForm";

export default function AlertsManager({ devices, userEmail, canEdit, onClose }: {
  devices: DevLite[]; userEmail: string; canEdit: (org: string) => boolean; onClose: () => void;
}) {
  const orgIds = useMemo(() => [...new Set(devices.map((d) => d.org_id).filter(Boolean) as string[])], [devices]);
  const [orgs, setOrgs] = useState<{ id: string; name: string }[]>([]);
  const [org, setOrg] = useState<string>(orgIds[0] ?? "");
  const [rules, setRules] = useState<AlertRule[] | null>(null);
  const [zones, setZones] = useState<Zone[]>([]);
  const [devFilter, setDevFilter] = useState("");
  const [edit, setEdit] = useState<AlertRule | "new" | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!orgIds.length) return;
    supabase.from("organisations").select("id, name").in("id", orgIds).order("name").then(({ data }) => {
      setOrgs(data ?? []);
      if (!org && data?.[0]) setOrg(data[0].id);
    });
  }, [orgIds]); // eslint-disable-line

  const load = useCallback(async () => {
    if (!org) return;
    const [{ data: r }, { data: z }] = await Promise.all([
      supabase.from("alert_rules").select("*").eq("org_id", org).order("created_at"),
      supabase.from("geofences").select("*").or(`org_id.eq.${org},is_global.eq.true`).order("name"),
    ]);
    setRules(((r ?? []) as AlertRule[]).sort((a, b) => Number(!!b.is_default) - Number(!!a.is_default)));
    setZones((z ?? []) as Zone[]);
  }, [org]);
  useEffect(() => { setRules(null); setDevFilter(""); load(); }, [load]);

  const orgDevices = devices.filter((d) => d.org_id === org).sort((a, b) => a.name.localeCompare(b.name));
  const devName = (id: string | null) => (id ? devices.find((d) => d.id === id)?.name ?? "one object" : "All objects");
  const shown = (rules ?? []).filter((r) => !devFilter || !r.device_id || r.device_id === devFilter);
  const may = canEdit(org);

  async function toggle(r: AlertRule) {
    try { await orgApi("PATCH", `alert-rules/${r.id}`, { enabled: !r.enabled }); await load(); } catch (e: any) { setErr(e.message); }
  }
  async function remove(r: AlertRule) {
    if (!confirm(r.is_default ? "Delete this default alert? It won't come back on its own (you can add it again)." : "Delete this alert?")) return;
    try { await orgApi("DELETE", `alert-rules/${r.id}`); await load(); } catch (e: any) { setErr(e.message); }
  }

  if (edit && org) return <AlertRuleForm org={org} rule={edit === "new" ? null : edit} zones={zones} userEmail={userEmail}
    devices={orgDevices} onClose={() => setEdit(null)} onSaved={async () => { setEdit(null); await load(); }} />;

  return (
    <Drawer title="Alerts" onClose={onClose}
      footer={may ? <button className="btn-primary" onClick={() => setEdit("new")}>+ New alert</button> : undefined}>
      <div className="am-filters">
        {orgs.length > 1 && <select value={org} onChange={(e) => setOrg(e.target.value)} aria-label="Company">
          {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
        </select>}
        <select value={devFilter} onChange={(e) => setDevFilter(e.target.value)} aria-label="Object">
          <option value="">All objects</option>
          {orgDevices.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
        </select>
      </div>
      {!may && <Notice kind="warn">Only the company owner or Skyward can change alerts. You'll still see them in the Events tab.</Notice>}
      {err && <Notice kind="bad" onClose={() => setErr(null)}>{err}</Notice>}
      <ul className="rule-list">
        {shown.map((r) => (
          <li key={r.id} className={r.enabled ? "" : "off"}>
            <div>
              <b>{r.name || ALERT_LABEL[r.type]}{r.is_default && <span className="tag">Default</span>}</b>
              <div className="sub">{ruleSummary(r, zones)} · {devName(r.device_id)}</div>
              <div className="sub">Alert by: {channelsText(r)}</div>
            </div>
            {may && <div className="rule-actions">
              <label className="switch" title={r.enabled ? "On" : "Off"}><input type="checkbox" checked={r.enabled} onChange={() => toggle(r)} aria-label={r.enabled ? "Switch off" : "Switch on"} /><span /></label>
              <button className="btn-ghost small" onClick={() => setEdit(r)}>Edit</button>
              <button className="btn-ghost small danger" onClick={() => remove(r)}>Delete</button>
            </div>}
          </li>
        ))}
        {rules && !shown.length && <li className="sub">No alerts yet{devFilter ? " for this object" : ""}.</li>}
        {!rules && <li className="sub">Loading…</li>}
      </ul>
      <p className="sub am-note">Zones are drawn with the Zones tool on the map toolbar.</p>
    </Drawer>
  );
}
